import gql from 'graphql-tag';
import { clients } from '../../util/clients';

const { auth } = clients.direct;
const usersMappingFields = 'id username roles disableUser fullname userWkst email phoneNumber deleted isSystemUser';

// Machine tokens: the credential a county workstation holds for Push to Cloud
// (RegovCountyToolkit). A token is its own record - no user account, no password,
// and it carries scopes rather than a person's roles. Never includes `hash`, and
// the printed token exists only in the createMachineToken response: there is no
// read-back path, so it is handed to the caller and never committed to the store.
const machineTokenFields = 'tokenId label scopes wkst status createdBy createdAt lastUsedAt pausedAt revokedAt';

// The mint mutation takes a GraphQL enum, so a scope crosses the wire as its enum
// NAME ('PUSH_GL_TAX') rather than its value ('push:gl-tax'). Mirrors the mapping
// in tmc-local-auth's graphql/MachineToken.js; the enum is what makes an unknown
// scope a schema error rather than something a resolver has to catch.
export const toScopeEnum = (scope) => String(scope).replace(/[:-]/g, '_').toUpperCase();

// Guided enrollment: a one-time code a workstation redeems for its own token, so nobody has to
// copy a 60-character credential off a screen. The code exists only in the
// createMachineTokenEnrollment response - same rule as the printed token, and for the same reason -
// and the row itself never carries it, only a digest the server looks up by.
const enrollmentConfigFields = 'pullEnabled pullSubdir pullLocalBasePath pullIntervalMinutes pushEnabled pushBasePath';
const machineEnrollmentFields = `enrollmentId label scopes wkst status createdBy source createdAt expiresAt claimedAt claimedFrom canceledAt tokenId config { ${enrollmentConfigFields} }`;

export const UserAdministrationActions = {
  async loadUsers({ dispatch, commit }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.query({
        query: gql`{
          auths {
            ${usersMappingFields}
          }
        }`,
        fetchPolicy: 'no-cache',
      });
      commit('setUsersList', results.data.auths);
      return results.data.auths;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem loading Users.', { root: true });
    }
    return {};
  },
  async userEnums({ dispatch, commit }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.query({
        query: gql`{
          UserRoleEnum:__type(name:"UserRoleEnum") {
            enumValues {
              name, description
            }
          },
        }`,
        fetchPolicy: 'no-cache',
      });
      clients.handleGQLErrors(results, commit);
      if (results.data && results.data.UserRoleEnum) {
        commit('setRolesList', results.data.UserRoleEnum.enumValues);
        commit('setRolesConst', results.data.UserRoleEnum.enumValues);
      }
    } catch (e) {
      dispatch('flashError', 'There was a problem getting User Enums.', { root: true });
    }
  },
  async upsertUser({ dispatch, state, commit }, updates) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.mutate({
        mutation: gql`mutation upsertUser ($user: AuthInputType)  {
          upsertAuth(upsert: $user) {
            ${usersMappingFields}
          }
        }`,
        variables: { user: updates },
      });
      clients.handleGQLErrors(results, commit);
      if (results.data && results.data.upsertAuth) {
        const replaceId = results.data.upsertAuth.id;
        const newTrans = state.userList.filter((t) => t.id !== replaceId);
        commit('setUsersList', [results.data.upsertAuth, ...newTrans]);
        dispatch('flashSuccess', 'User has successfully saved.', { root: true });
      }
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem to create/update the user (username might be duplicate).', { root: true });
    }
  },
  async deleteUser({ dispatch, state, commit }, user) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.mutate({
        mutation: gql`mutation deleteAuth ($id: ID!)  {
          deleteAuth(id: $id) {
            id
            deleted
          }
        }`,
        variables: {
          id: user.id,
        },
      });
      clients.handleGQLErrors(results, commit);
      if (results.data && results.data.deleteAuth) {
        const deletedID = results.data.deleteAuth.id;
        const newTrans = state.userList.filter((t) => t.id !== deletedID);
        commit('setUsersList', newTrans);
        dispatch('flashSuccess', 'User has been removed. They no longer appear in this list and cannot sign in.', { root: true });
      }
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem deleting the department.', { root: true });
    }
  },
  async resetUsersPassword({ dispatch, commit }, updates) {
    let results;
    let resultData = {};
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.mutate({
        mutation: gql`mutation resetUsersPassword ($changingPassUser: String, $newPassword: String)  {
          resetUsersPassword(changingPassUser: $changingPassUser, newPassword: $newPassword) {
            message status deleted tokenContext { jwt, username, status }
          }
        }`,
        variables: updates,
      });
      clients.handleGQLErrors(results, commit);
      if (results.data && results.data.resetUsersPassword) {
        resultData = results.data.resetUsersPassword;
        if (resultData.status) {
          dispatch('flashSuccess', resultData.message, { root: true });
        } else if (resultData.deleted) {
          dispatch('flashError', resultData.message || 'This account has been removed.', { root: true });
        } else {
          dispatch('flashError', resultData.message, { root: true });
        }
      }
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem resetting password.', { root: true });
    }
    return resultData;
  },
  async loadMachineTokens({ dispatch, commit }, { status, includeRevoked = false } = {}) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.query({
        query: gql`query machineTokens ($status: String, $includeRevoked: Boolean) {
          machineTokens(status: $status, includeRevoked: $includeRevoked) {
            ${machineTokenFields}
          }
        }`,
        variables: { status, includeRevoked },
        fetchPolicy: 'no-cache',
      });
      clients.handleGQLErrors(results, commit);
      const tokens = (results.data && results.data.machineTokens) || [];
      commit('setMachineTokens', tokens);
      return tokens;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem loading machine tokens.', { root: true });
    }
    return [];
  },
  /** The scopes a token may be granted, so the mint form builds its picker from the server. */
  async loadMachineTokenScopes({ dispatch, commit }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.query({
        query: gql`{ machineTokenScopes }`,
        fetchPolicy: 'no-cache',
      });
      clients.handleGQLErrors(results, commit);
      const scopes = (results.data && results.data.machineTokenScopes) || [];
      commit('setMachineTokenScopes', scopes);
      return scopes;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem loading the machine token scopes.', { root: true });
    }
    return [];
  },
  /**
   * Mints a token and returns { token, machineToken } to the caller. The printed token is the
   * response's only copy: it is shown once and cannot be recovered, so it is deliberately not
   * committed to the store, which every component can read and which survives navigation.
   */
  async createMachineToken({ dispatch, commit }, { label, scopes, wkst } = {}) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.mutate({
        mutation: gql`mutation createMachineToken (
          $label: String!, $scopes: [MachineScopeEnum!]!, $wkst: Int
        ) {
          createMachineToken(label: $label, scopes: $scopes, wkst: $wkst) {
            token
            machineToken { ${machineTokenFields} }
          }
        }`,
        variables: { label, scopes: (scopes || []).map(toScopeEnum), wkst },
      });
      clients.handleGQLErrors(results, commit);
      const minted = results.data && results.data.createMachineToken;
      if (minted && minted.token) {
        await dispatch('loadMachineTokens', { includeRevoked: true });
        dispatch('flashSuccess', 'Machine token created. Copy it now - it cannot be shown again.', { root: true });
        return minted;
      }
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      // The server's reason is the useful part: an unknown scope or a missing label are both
      // things an admin can act on.
      const reason = (e && e.message) ? e.message.replace('GraphQL error: ', '') : '';
      dispatch('flashError', `Could not create a machine token. ${reason}`, { root: true });
    }
    return null;
  },
  /**
   * pause / resume / revoke. Pause is the reversible one - a desk being rebuilt, a clerk on leave -
   * and revoke is permanent, with the row kept as the record of what was cut off and when.
   */
  async machineTokenLifecycle({ dispatch, commit }, { action, tokenId } = {}) {
    const mutations = {
      pause: {
        name: 'pauseMachineToken',
        done: 'Machine token paused. That workstation stops uploading when its sign-in expires.',
      },
      resume: { name: 'resumeMachineToken', done: 'Machine token resumed.' },
      revoke: {
        name: 'revokeMachineToken',
        done: 'Machine token revoked. That workstation stops uploading when its sign-in expires.',
      },
    };
    const chosen = mutations[action];
    if (!chosen) {
      dispatch('flashError', `Unknown machine token action: ${action}`, { root: true });
      return false;
    }
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.mutate({
        mutation: gql`mutation ${chosen.name} ($tokenId: String!) {
          ${chosen.name}(tokenId: $tokenId) { ${machineTokenFields} }
        }`,
        variables: { tokenId },
      });
      clients.handleGQLErrors(results, commit);
      await dispatch('loadMachineTokens', { includeRevoked: true });
      dispatch('flashSuccess', chosen.done, { root: true });
      return true;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      const reason = (e && e.message) ? e.message.replace('GraphQL error: ', '') : '';
      dispatch('flashError', `Could not ${action} the machine token. ${reason}`, { root: true });
    }
    return false;
  },
  async deleteMachineToken({ dispatch, commit }, { tokenId } = {}) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.mutate({
        mutation: gql`mutation deleteMachineToken ($tokenId: String!) {
          deleteMachineToken(tokenId: $tokenId)
        }`,
        variables: { tokenId },
      });
      clients.handleGQLErrors(results, commit);
      await dispatch('loadMachineTokens', { includeRevoked: true });
      dispatch('flashSuccess', 'Machine token deleted.', { root: true });
      return true;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      // The server refuses to delete anything but a revoked token, and says so.
      const reason = (e && e.message) ? e.message.replace('GraphQL error: ', '') : '';
      dispatch('flashError', `Could not delete the machine token. ${reason}`, { root: true });
    }
    return false;
  },
  /**
   * Codes that have not been used yet. Claimed and cancelled ones are history and are opt-in, the
   * same way revoked tokens are: what an admin has this page open to ask is which codes are still
   * out there waiting for a desk.
   */
  async loadMachineTokenEnrollments({ dispatch, commit }, {
    includeClaimed = false, includeCanceled = false,
  } = {}) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.query({
        query: gql`query machineTokenEnrollments ($includeClaimed: Boolean, $includeCanceled: Boolean) {
          machineTokenEnrollments(includeClaimed: $includeClaimed, includeCanceled: $includeCanceled) {
            ${machineEnrollmentFields}
          }
        }`,
        variables: { includeClaimed, includeCanceled },
        fetchPolicy: 'no-cache',
      });
      clients.handleGQLErrors(results, commit);
      const enrollments = (results.data && results.data.machineTokenEnrollments) || [];
      commit('setMachineTokenEnrollments', enrollments);
      return enrollments;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem loading workstation setup codes.', { root: true });
    }
    return [];
  },
  /**
   * Issues a code and returns { code, enrollment } to the caller. Nothing has been minted yet - the
   * token is generated when a desk redeems the code - so losing the code costs nothing: cancel the
   * enrollment and issue another. It is still kept out of the store, which every component can read
   * and which survives navigation.
   */
  async createMachineTokenEnrollment({ dispatch, commit }, {
    label, scopes, wkst, config,
  } = {}) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.mutate({
        mutation: gql`mutation createMachineTokenEnrollment (
          $label: String!, $scopes: [MachineScopeEnum!]!, $wkst: Int,
          $config: MachineEnrollmentConfigInput
        ) {
          createMachineTokenEnrollment(label: $label, scopes: $scopes, wkst: $wkst, config: $config) {
            code
            enrollment { ${machineEnrollmentFields} }
          }
        }`,
        variables: {
          label, scopes: (scopes || []).map(toScopeEnum), wkst, config,
        },
      });
      clients.handleGQLErrors(results, commit);
      const created = results.data && results.data.createMachineTokenEnrollment;
      if (created && created.code) {
        await dispatch('loadMachineTokenEnrollments');
        return created;
      }
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      const reason = (e && e.message) ? e.message.replace('GraphQL error: ', '') : '';
      dispatch('flashError', `Could not create a workstation setup code. ${reason}`, { root: true });
    }
    return null;
  },
  /**
   * Self-service: the signed-in user sets up the computer they are at. Open to every role - the
   * auth service fixes the scopes, the lifetime and the per-user daily limit, so unlike the admin
   * action above this one takes only a workstation number and an optional label. Returns
   * { code, wkst, label, expiresAt }, or null after flashing the server's reason (the daily limit
   * message is written for the person reading it). The code stays out of the store, as above.
   */
  async createMyWorkstationSetup({ dispatch, commit }, { wkst, label } = {}) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.mutate({
        mutation: gql`mutation createMyWorkstationSetup ($wkst: Int!, $label: String) {
          createMyWorkstationSetup(wkst: $wkst, label: $label) {
            code wkst label expiresAt
          }
        }`,
        variables: { wkst, label },
      });
      clients.handleGQLErrors(results, commit);
      const created = results.data && results.data.createMyWorkstationSetup;
      if (created && created.code) {
        return created;
      }
      // An answer with errors but no data, in case the client is ever set to hand those back
      // rather than throw: the person is owed the reason (the daily limit is worded for them).
      const reported = results.errors && results.errors[0] && results.errors[0].message;
      if (reported) {
        dispatch('flashError', `Could not install the ReGov Toolkit. ${reported}`, { root: true });
      }
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      const reason = (e && e.message) ? e.message.replace('GraphQL error: ', '') : '';
      dispatch('flashError', `Could not install the ReGov Toolkit. ${reason}`, { root: true });
    }
    return null;
  },
  /** Kills an unclaimed code. The server refuses once it has been claimed - revoke the token. */
  async cancelMachineTokenEnrollment({ dispatch, commit }, { enrollmentId } = {}) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await auth.mutate({
        mutation: gql`mutation cancelMachineTokenEnrollment ($enrollmentId: String!) {
          cancelMachineTokenEnrollment(enrollmentId: $enrollmentId) { ${machineEnrollmentFields} }
        }`,
        variables: { enrollmentId },
      });
      clients.handleGQLErrors(results, commit);
      await dispatch('loadMachineTokenEnrollments');
      dispatch('flashSuccess', 'Setup code cancelled. It can no longer set up a workstation.', { root: true });
      return true;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      const reason = (e && e.message) ? e.message.replace('GraphQL error: ', '') : '';
      dispatch('flashError', `Could not cancel the setup code. ${reason}`, { root: true });
    }
    return false;
  },
  async uploadMkdir({ dispatch }) {
    try {
      await dispatch('reAuth', null, { root: true });
      await clients.direct.backendRest.get('/user/uploadMkdir');
    } catch (e) {
      dispatch('logException', e, { root: true });
      dispatch('flashError', 'There was a problem setting up a user\'s upload folder.', { root: true });
    }
  },
};

export default UserAdministrationActions;
